import React, { FC, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Checkbox } from 'src/components/widgets/checkbox';
import { T } from 'src/shared/helpers';
import { TOOL_DEFINITIONS } from '../common/constants';
import UsageControls, { UsageControlsRef } from 'src/components/widgets/usageControls/usageControls';
import type { aiImageGenEditor_Query } from './__generated__/aiImageGenEditor_Query.graphql';
import type { aiImageGenEditor_CreateMutation } from './__generated__/aiImageGenEditor_CreateMutation.graphql';
import type { aiImageGenEditor_UpdateMutation } from './__generated__/aiImageGenEditor_UpdateMutation.graphql';
import {
    StackedBreadcrumb,
    StackedBody,
    SectionCard,
    SectionCardTitle,
    FormDivider,
    FormLabelCompact,
    CompactSwitchGrid,
    CompactSwitchRow,
    CompactSwitchLabel,
    InfoCallout
} from '../common/styled';
import styled from 'styled-components';
import { PanelLinesSkeleton } from '../common/skeletons';

export interface AIImageGenEditorProps {
    itemId: string;
    productId: string;
    toolKey: string;
    onBack: () => void;
    onSave: () => void;
    saveRef: React.MutableRefObject<(() => Promise<void>) | null>;
    onCanSaveChange?: (canSave: boolean) => void;
}

const CompactSelect = styled.select`
    padding: 4px 8px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 4px;
    font-size: 12px;
`;

const CompactControls = styled.div`
    font-size: 12px;
    label,
    span,
    input,
    select {
        font-size: 12px;
    }
`;

const AIImageGenEditorContent: FC<AIImageGenEditorProps & { fetchKey: string }> = ({
    productId,
    toolKey,
    onBack,
    onSave,
    saveRef,
    onCanSaveChange,
    fetchKey
}) => {
    const toolName = TOOL_DEFINITIONS.find((tool) => tool.key === toolKey)?.name || '';
    const usageControlsRef = useRef<UsageControlsRef>(null);
    const productNameData = useLazyLoadQuery<any>(
        graphql`
            query aiImageGenEditor_ProductNameQuery($id: ID!) {
                node(id: $id) {
                    ... on Model {
                        name
                    }
                }
            }
        `,
        { id: productId }
    );
    const productName = (productNameData?.node as any)?.name || '';

    const data = useLazyLoadQuery<aiImageGenEditor_Query>(
        graphql`
            query aiImageGenEditor_Query {
                imageGeneration {
                    id
                    numberOfImagesGeneration
                    numberOfImagesEdit
                    categoriesForGeneration
                    categoriesForEdit
                    showInUpload
                    showInVerticalBar
                    showInImages
                }
                imageGenerationPresetCategories {
                    categoryID
                    name
                }
            }
        `,
        {},
        { fetchPolicy: 'network-only', fetchKey }
    );

    const existing = data.imageGeneration ?? null;
    const categories = data.imageGenerationPresetCategories ?? [];

    const [generationActive, setGenerationActive] = useState(() => (existing?.numberOfImagesGeneration ?? 0) > 0);
    const [editActive, setEditActive] = useState(() => (existing?.numberOfImagesEdit ?? 0) > 0);
    const [numImagesGeneration, setNumImagesGeneration] = useState(() => existing?.numberOfImagesGeneration ?? 1);
    const [numImagesEdit, setNumImagesEdit] = useState(() => existing?.numberOfImagesEdit ?? 1);
    const [categoriesForGeneration, setCategoriesForGeneration] = useState<readonly number[]>(() => existing?.categoriesForGeneration ?? []);
    const [usageControlsValid, setUsageControlsValid] = useState(true);

    const [commitCreate] = useMutation<aiImageGenEditor_CreateMutation>(graphql`
        mutation aiImageGenEditor_CreateMutation($input: ImageGenerationCreateInput!) {
            createImageGeneration(input: $input) {
                id
            }
        }
    `);

    const [commitUpdate] = useMutation<aiImageGenEditor_UpdateMutation>(graphql`
        mutation aiImageGenEditor_UpdateMutation($input: ImageGenerationUpdateInput!) {
            updateImageGeneration(input: $input) {
                id
            }
        }
    `);

    const handleSave = useCallback(async () => {
        if (usageControlsRef.current?.hasChanges()) {
            usageControlsRef.current.save();
        }

        const input = {
            numberOfImagesGeneration: generationActive ? numImagesGeneration : 0,
            numberOfImagesEdit: editActive ? numImagesEdit : 0,
            categoriesForGeneration: generationActive ? [...categoriesForGeneration] : [],
            categoriesForEdit: editActive ? [1] : [],
            showInUpload: existing?.showInUpload ?? true,
            showInVerticalBar: existing?.showInVerticalBar ?? false,
            showInImages: existing?.showInImages ?? false
        };

        if (existing?.id) {
            await new Promise<void>((resolve, reject) => {
                commitUpdate({
                    variables: { input: { id: existing.id, ...input } },
                    onCompleted: () => {
                        onSave();
                        resolve();
                    },
                    onError: (error) => reject(error)
                });
            });
        } else {
            await new Promise<void>((resolve, reject) => {
                commitCreate({
                    variables: { input },
                    onCompleted: () => {
                        onSave();
                        resolve();
                    },
                    onError: (error) => reject(error)
                });
            });
        }
    }, [
        generationActive,
        editActive,
        numImagesGeneration,
        numImagesEdit,
        categoriesForGeneration,
        existing,
        onSave,
        commitCreate,
        commitUpdate,
        usageControlsValid
    ]);

    saveRef.current = handleSave;

    const handleGenerationActiveChange = (checked: boolean) => {
        setGenerationActive(checked);
        if (checked && numImagesGeneration <= 0) {
            setNumImagesGeneration(1);
        }
        if (checked && categories.length > 0 && categoriesForGeneration.length === 0) {
            setCategoriesForGeneration(categories.map((category) => category.categoryID));
        }
    };

    const handleEditActiveChange = (checked: boolean) => {
        setEditActive(checked);
        if (checked && numImagesEdit <= 0) {
            setNumImagesEdit(1);
        }
    };

    useEffect(() => {
        onCanSaveChange?.((generationActive || editActive) && usageControlsValid);
    }, [generationActive, editActive, usageControlsValid, onCanSaveChange]);

    return (
        <>
            <StackedBreadcrumb>
                <span onClick={onBack}>{productName}</span> / {toolName}
            </StackedBreadcrumb>
            <StackedBody>
                <InfoCallout>{T._('This tool applies to all products.', 'Admin')}</InfoCallout>

                <SectionCard>
                    <SectionCardTitle>{T._('Settings', 'Admin')}</SectionCardTitle>

                    <FormDivider>
                        <FormLabelCompact>{T._('Generation', 'Admin')}</FormLabelCompact>
                        <CompactSwitchGrid>
                            <CompactSwitchRow>
                                <Checkbox
                                    variant='compact'
                                    label={T._('Enable "AI Image Generation"', 'Admin')}
                                    checked={generationActive}
                                    onChange={handleGenerationActiveChange}
                                />
                            </CompactSwitchRow>
                        </CompactSwitchGrid>
                    </FormDivider>

                    {generationActive && (
                        <>
                            <div style={{ paddingLeft: 24 }}>
                                <CompactSwitchGrid>
                                    {categories.map((cat) => (
                                        <CompactSwitchRow key={cat.categoryID}>
                                            <Checkbox
                                                variant='compact'
                                                label={cat.name === 'Image' ? T._('Images', 'Admin') : cat.name}
                                                checked={categoriesForGeneration.includes(cat.categoryID)}
                                                onChange={(checked) => {
                                                    if (checked) {
                                                        setCategoriesForGeneration((prev) => [...prev, cat.categoryID]);
                                                    } else {
                                                        setCategoriesForGeneration((prev) => prev.filter((id) => id !== cat.categoryID));
                                                        if (categoriesForGeneration.length === 1 && categoriesForGeneration[0] === cat.categoryID) {
                                                            setGenerationActive(false);
                                                        }
                                                    }
                                                }}
                                            />
                                        </CompactSwitchRow>
                                    ))}
                                </CompactSwitchGrid>
                            </div>

                            <CompactSwitchRow>
                                <CompactSwitchLabel>{T._('Output per prompt', 'Admin')}</CompactSwitchLabel>
                                <CompactSelect value={numImagesGeneration} onChange={(e) => setNumImagesGeneration(Number(e.target.value))}>
                                    {[1, 2, 3, 4].map((n) => (
                                        <option key={n} value={n}>
                                            {n} {n === 1 ? 'image' : 'images'}
                                        </option>
                                    ))}
                                </CompactSelect>
                            </CompactSwitchRow>
                        </>
                    )}

                    <FormDivider>
                        <FormLabelCompact>{T._('Edit', 'Admin')}</FormLabelCompact>
                        <CompactSwitchGrid>
                            <CompactSwitchRow>
                                <Checkbox
                                    variant='compact'
                                    label={T._('Enable "AI Image Edit"', 'Admin')}
                                    checked={editActive}
                                    onChange={handleEditActiveChange}
                                />
                            </CompactSwitchRow>
                        </CompactSwitchGrid>
                    </FormDivider>

                    {editActive && (
                        <CompactSwitchRow>
                            <CompactSwitchLabel>{T._('Output per prompt', 'Admin')}</CompactSwitchLabel>
                            <CompactSelect value={numImagesEdit} onChange={(e) => setNumImagesEdit(Number(e.target.value))}>
                                {[1, 2, 3, 4].map((n) => (
                                    <option key={n} value={n}>
                                        {n} {n === 1 ? 'image' : 'images'}
                                    </option>
                                ))}
                            </CompactSelect>
                        </CompactSwitchRow>
                    )}
                </SectionCard>

                <SectionCard>
                    <SectionCardTitle>{T._('Usage Controls', 'Admin')}</SectionCardTitle>
                    <CompactControls>
                        <Suspense fallback={null}>
                            <UsageControls ref={usageControlsRef} toolType='CustomizerAIImageGenerator' onValidChange={setUsageControlsValid} />
                        </Suspense>
                    </CompactControls>
                </SectionCard>
            </StackedBody>
        </>
    );
};

export const AIImageGenEditor: FC<AIImageGenEditorProps> = (props) => {
    // Per-mount fetchKey: Relay dedupes active queries within a TTL window after unmount,
    // so navigating away and returning quickly would serve cached data even with network-only.
    // Declared here (above the Suspense boundary) so the initializer isn't re-run on suspend retries.
    const [fetchKey] = useState(() => Date.now().toString());
    return (
        <Suspense
            fallback={
                <>
                    <StackedBreadcrumb>
                        <span onClick={props.onBack}>{T._('Loading...', 'Admin')}</span>
                    </StackedBreadcrumb>
                    <StackedBody>
                        <PanelLinesSkeleton />
                    </StackedBody>
                </>
            }
        >
            <AIImageGenEditorContent {...props} fetchKey={fetchKey} />
        </Suspense>
    );
};
